import { Activity, BadgeCheck, Building2, Settings, ClipboardCheck, FileWarning, Info, Megaphone, Plus, Store, Ticket, UserX, UsersRound } from 'lucide-react';
import { NAV, isHiddenWhenTrimmed, isOffThisDeployment } from './nav';
import { SETTINGS_CARDS } from './settings';
import { TOOLS } from './utilityTools';

// ⌘K — every destination, and only the actions that actually DO something.
//
// TWO RULES, and the old list broke both.
//
// 1. NAVIGATION IS DERIVED, NOT COPIED. The page list was a second, hand-maintained copy of
//    the sidebar and had drifted badly: it still offered "Risk & Compliance", which was
//    deliberately commented out of the sidebar, while missing Reports, Support, Call Center,
//    Chat, Funding, Subscriptions, Ticketing & Inventory, Products, Accreditation, Utilities,
//    Backend Map, Reality Map and Release Notes — thirteen real destinations you could not
//    reach from the one control built for reaching things. Deriving from NAV means the two
//    can never disagree again, a new sidebar entry is ⌘K-reachable the day it lands, and
//    removing one removes it here too.
//
// 2. AN ACTION MUST ACT. Six of the twelve "quick actions" navigated somewhere and then
//    toasted an apology — "Use the Initiate payout button on the page", "Click + Pair
//    integration on the page", "Open an event to compose a broadcast". A command whose
//    payload is instructions for finding the real command is not a command; it is a page
//    link wearing a verb, and the page links are already in the list above it. Worse:
//    "New risk rule" set a flag true and false again 100ms later, and the two Export
//    entries were the same handler opening a modal counting MOCK rows. All removed. What
//    remains opens something real, immediately, wherever you are.

// Sections keep the sidebar's own grouping, so the palette reads like the app.
const PAGE_ENTRIES = NAV.flatMap((group) =>
  group.items.map((item) => ({
    kind: 'page',
    area: item.id,
    id: item.id,
    label: item.label,
    sub: group.section ? group.section.toLowerCase().replace(/&/g, 'and') : 'home',
    // The page's own synonyms, declared on its nav entry — they were written and never searched.
    keywords: item.keywords || '',
    icon: item.icon,
  })),
);

// Every tab of every page, from the same nav entries the pages render — "Affiliates" is a tab
// inside Sales Setup, and a palette that only knew pages could not find it.
const TAB_ENTRIES = NAV.flatMap((group) =>
  group.items.flatMap((item) => (item.tabs || []).filter((t) => !t.disabledReason).map((t) => ({
    kind: 'page',
    area: item.id,
    hideWhenTrimmed: !!t.hideWhenTrimmed,
    id: item.id,
    search: { [item.tabParam || 'tab']: t.id },
    label: t.label,
    sub: item.label,
    keywords: t.keywords || '',
    icon: item.icon,
  }))),
);

// Settings sections, from the settings registry itself. Drawer-mode cards open in place on the
// Settings page and have no address, so only page-mode ones are listed.
const SETTINGS_ENTRIES = SETTINGS_CARDS.filter((c) => c.mode === 'page').map((c) => ({
  kind: 'page', area: 'settings', id: 'settings', search: { section: c.id }, label: c.label, sub: `Settings · ${c.sub || c.group}`, icon: Settings,
}));

// Every Utilities tool, from the list the Utilities page renders (constants/utilityTools) — "print service",
// "background jobs", "heap" were a click into Utilities and a scan of its grid away, and not findable here at all.
// A tool that lives outside the app (`href`) opens in a new tab, like its card.
const UTILITY_ENTRIES = TOOLS.map((t) => ({
  kind: 'page',
  area: 'utilities',
  ...(t.href ? { href: t.href } : { id: 'utilities', search: { tab: t.id } }),
  label: t.label,
  sub: `Utilities · ${t.group}${t.href ? ' · opens in a new tab' : ''}`,
  keywords: `${t.keyword || ''} ${t.sub || ''}`,
  icon: t.icon,
}));

// Real routes deliberately kept OUT of the sidebar chrome — reachable, useful, and exactly
// what a command palette is for: the things a regular knows exist and a newcomer shouldn't
// be given as a permanent menu item.
//
// /risk is the case in point and the rule has been sharpened rather than broken: the PAGE is
// still not offered anywhere, because most of what it used to show was never built. But two of
// its tabs are live and hold real work, so those two tabs are listed — a destination here is a
// working SURFACE, which is not always a whole route.
// A ROUTE EXISTING IS NOT THE SAME AS THE PAGE WORKING, and the first version of this list
// made exactly that mistake — it offered anything with an entry in the route table. Each of
// these was then checked for whether it reads real data, and three were dropped:
//
//   reconciliation — rendered MOCK_RECON_RUNS / _RECORDS / _RULES / _AUDIT; its address now opens Transactions.
//   wallets        — balances computed from guesses (paid-out shown as "Available", a flat 4.5% fee); opens Treasury.
//   team           — roles come from SEED_ROLES in data/settings; no backend call.
// (health was on this list when it read the MOCK object; it now asks every deployed backend's /health, so it is offered.)
//
// Offering those is worse than omitting them: ⌘K is where someone goes when they trust the
// console to know what exists, so a fabricated page reached that way reads as real.
// A subtitle's job is to tell you whether this is the thing you're looking for. These read
// "not in the sidebar · …", which answers a question nobody asked — where the page ISN'T —
// and spends the front of the line, the only part scanned while typing, on it. Say what the
// page shows.
const UNLISTED_PAGE_ENTRIES = [
  { kind: 'page', id: 'health',           label: 'System health',      sub: 'Every deployed backend — up, how fast, which build', keywords: 'status uptime deployments backend', icon: Activity },
  { kind: 'page', id: 'issues-approvals', label: 'Issues & Approvals', sub: 'Actions waiting on a second operator to approve',          icon: ClipboardCheck },
  { kind: 'page', id: 'season-passes', hideWhenTrimmed: true, label: 'Season Passes',      sub: 'Passes that admit to a whole series of events',            icon: Ticket },
  { kind: 'page', id: 'users',            label: 'Users',              sub: 'Operator accounts and their roles',                        icon: UsersRound },
  // TWO TABS, NOT A PAGE. Both live inside /risk, whose other tabs were empty shells and are no
  // longer offered — so the page itself stays out of the sidebar while the two surfaces that
  // hold real, actionable work become first-class destinations here. `search` puts you on the
  // tab, not on the page's default.
  //
  // Data-subject requests are STATUTORY and carry a 30-day deadline; measured on prod there are
  // three open, the oldest 101 days overdue. A legal clock should not be four clicks and a
  // remembered URL away.
  { kind: 'page', id: 'risk', search: { tab: 'Data requests' },    label: 'Data requests',  sub: 'Access and erasure requests · statutory 30-day deadline', icon: FileWarning },
  { kind: 'page', id: 'risk', search: { tab: 'KYC & compliance' }, label: 'Organizer KYC',  sub: 'Verification documents awaiting review',                   icon: BadgeCheck },
];

// Every one of these opens a real modal on the spot, from anywhere, and completes there.
const ACTION_ENTRIES = [
  // The same wizard as the Events page's New event button and the header's Quick actions.
  { kind: 'action', area: 'events', action: 'new-event',           label: 'New event',           sub: 'Event-page creation wizard',                    icon: Plus,       keywords: 'create add event page' },
  { kind: 'action', area: 'orders', action: 'new-manual-order',    label: 'New manual order',    sub: 'Customer, tickets, payment · issues on OTP',    icon: Plus,       keys: 'n o' },
  { kind: 'action', area: 'organizers', action: 'onboard-organizer',   label: 'Onboard organizer',   sub: 'Profile, contacts, settlement details',         icon: Building2 },
  { kind: 'action', area: 'channels', action: 'new-box-office',      label: 'Create box office',   sub: 'A selling point with its own operators',        icon: Store },
  { kind: 'action', area: 'accreditation', action: 'issue-accreditation', label: 'Issue accreditation', sub: 'Crew, press or guest pass for an event',        icon: BadgeCheck },
  // Named for what it does: it saves a DRAFT. Executing the blast is a separate,
  // confirm-gated click on the campaign row — "Send campaign" promised the wrong thing.
  { kind: 'action', area: 'design', action: 'send-campaign',       label: 'New campaign',        sub: 'Audience, template, review · saved as a draft', icon: Megaphone },
];

const HELP_ENTRIES = [
  { kind: 'help', action: 'show-help', label: 'Keyboard shortcuts', sub: 'Every chord, in one place', icon: Info, keys: '?' },
  // The real panel (SupportNeedsHelpPanel — /api/needs-help), not a fresh page you'd
  // still have to click a tab to reach: customers with a failed/pending order in the
  // last 7 days, one tap from "Reach out" opening a real ticket on their number.
  { kind: 'help', area: 'support', action: 'needs-help', label: 'Needs help', sub: 'Customers stuck on a failed or pending order — reach out to recover the sale', icon: UserX },
];

/**
 * WHAT ⌘K OFFERS THIS OPERATOR — the same trimming as the sidebar (lib/accessGating): on the trimmed view an
 * entry in a hidden area (its page, its tabs, its tools, its actions) or flagged hideWhenTrimmed is not offered.
 * Without this, ⌘K reached every page and tab the sidebar hid. Full-access operators get everything.
 */
export const paletteEntriesFor = (trimmed) => COMMAND_PALETTE_ENTRIES
  // An area this deployment does not have at all (partners on MoMo) is offered to nobody (constants/nav).
  .filter((e) => !(e.area && isOffThisDeployment(e.area)))
  .filter((e) => !trimmed || (!e.hideWhenTrimmed && !(e.area && isHiddenWhenTrimmed(e.area))));

export const COMMAND_PALETTE_ENTRIES = [
  ...PAGE_ENTRIES,
  ...TAB_ENTRIES,
  ...SETTINGS_ENTRIES,
  ...UTILITY_ENTRIES,
  ...UNLISTED_PAGE_ENTRIES,
  ...ACTION_ENTRIES,
  ...HELP_ENTRIES,
];
